import type { ConfirmationOptions } from 'primevue/confirmationoptions';
import { useConfirm } from 'primevue/useconfirm';
import { onUnmounted } from 'vue';
import type { StyledConfirmationOptions } from '~/types/confirm-dialog';

/** The confirmation open now, as the `require` that opened it marked it, or null while none is. */
let openConfirmation: symbol | null = null;

/**
 * PrimeVue's `useConfirm`, for a confirmation that goes when the component that asked it does.
 *
 * A confirmation renders in the layout's `<AppConfirmDialog/>` (`components/layout/AppConfirmDialog.vue`),
 * not in the component that asks it, so on its own it outlives that component: after Back or Forward
 * it stays open over the next page, and on a business page its accept is refused by the tenant scope
 * that died with the page it asked about. PrimeVue's own `close` cannot be what an unmount calls: one
 * dialog host serves every caller, and `close` closes whichever confirmation is open, another
 * component's included.
 *
 * So the component that asked closes its confirmation when it unmounts, and only while that
 * confirmation is still the one open. Every confirmation in the app is opened through here, which
 * makes `openConfirmation` the truth about whose is open: a `require` from any caller takes it over,
 * and accepting, rejecting or dismissing a confirmation (its mask, Escape) ends it. The caller's own
 * `accept`, `reject` and `onHide` run exactly as given.
 */
export const usePageConfirm = (): { require: (options: StyledConfirmationOptions) => void } => {
    const confirm = useConfirm();
    // The confirmation this component opened last.
    let asked: symbol | null = null;

    onUnmounted(() => {
        if (asked === null || asked !== openConfirmation) return;
        openConfirmation = null;
        confirm.close();
    });

    return {
        require(options) {
            const confirmation = Symbol('confirmation');
            // What the handler returns is handed back, so an async `accept` is still the caller's promise.
            const settling = (handler: (() => void) | undefined) => () => {
                if (openConfirmation === confirmation) openConfirmation = null;
                return handler?.();
            };

            asked = confirmation;
            openConfirmation = confirmation;
            // The shared dialog renders `icon` as a component and reads `iconVariant`, which
            // PrimeVue's own options type as a class name and leave out (`StyledConfirmationOptions`).
            confirm.require({
                ...options,
                accept: settling(options.accept),
                reject: settling(options.reject),
                onHide: settling(options.onHide),
            } as unknown as ConfirmationOptions);
        },
    };
};
